import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {api} from './Api.js';
import Statistics from './Statistics.js';
import SummaryCard from './SummaryCard.js';

const LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];
const LEVEL_FIELDS = [['vocabularyLevel', 'Vocabulary'], ['writingLevel', 'Writing'], ['speakingLevel', 'Speaking'], ['grammarLevel', 'Grammar']];

const User = ({showNotification}) => {
  const [info, setInfo] = useState(null);
  const [levels, setLevels] = useState(null);
  const [remindingWords, setRemindingWords] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [activeTab, setActiveTab] = useState('profile');
  const [waiting, setWaiting] = useState(false);
  const statisticsLoadingRef = useRef(false);
  const navigate = useNavigate();

  const fetchInfo = useCallback(async () => {
    const response = await api.fetchUser(navigate, showNotification);
    if (api.isValidResponse(response)) {
      setInfo(response.data);
      setLevels({vocabularyLevel: response.data.vocabularyLevel, writingLevel: response.data.writingLevel, speakingLevel: response.data.speakingLevel, grammarLevel: response.data.grammarLevel});
    }
  }, [navigate, showNotification]);
  const fetchRemindingWords = useCallback(async (userId) => {
    const response = await api.getRemindingWords({userId, status: 'SUCCESS', sort: {direction: 'ASC', property: 'REMIND_DATE'}, limit: 10}, navigate, showNotification);
    if (api.isValidResponse(response)) setRemindingWords(response.data);
  }, [navigate, showNotification]);
  const fetchStatistics = useCallback(async () => {
    if (statisticsLoadingRef.current) return;
    statisticsLoadingRef.current = true;
    const response = await api.fetchSummaryStatistics(navigate, showNotification);
    if (api.isValidResponse(response)) setStatistics(response.data);
  }, [navigate, showNotification]);
  useEffect(() => {
    if (!info) fetchInfo();
  }, [info, fetchInfo]);
  useEffect(() => {
    if (info && !remindingWords) fetchRemindingWords(info.id);
    if (info && !statistics) fetchStatistics();
  }, [info, remindingWords, statistics, fetchRemindingWords, fetchStatistics]);
  const levelUpdated = info && levels && LEVEL_FIELDS.some(([key]) => levels[key] !== info[key]);
  const saveLevels = useCallback(async () => {
    setWaiting(true);
    const response = await api.changeLevel(levels, navigate, showNotification);
    if (api.isValidResponse(response)) {
      setInfo((current) => ({...current, ...levels}));
      showNotification('Levels updated');
    }
    setWaiting(false);
  }, [levels, navigate, showNotification]);
  const logout = useCallback(async () => {
    setWaiting(true);
    const response = await api.logout(navigate, showNotification);
    if (api.isValidResponse(response)) navigate('/login');
    setWaiting(false);
  }, [navigate, showNotification]);

  if (!info || !levels || !remindingWords || !statistics) return <div className="page-loader"><span className="loader-dot"/> Loading your profile…</div>;
  const words = remindingWords.content || [];
  return <main className="page-container profile-page">
    <div className="section-heading"><span className="eyebrow">Your profile</span><h1>Hi, {info.username}</h1><p>Set your levels and keep an eye on the skills you are building.</p></div>
    <SummaryCard summary={statistics}/>
    <div className="profile-tabs" role="tablist" aria-label="Profile sections">
      <button className={activeTab === 'profile' ? 'is-active' : ''} role="tab" aria-selected={activeTab === 'profile'} onClick={() => setActiveTab('profile')}>Settings</button>
      <button className={activeTab === 'statistics' ? 'is-active' : ''} role="tab" aria-selected={activeTab === 'statistics'} onClick={() => setActiveTab('statistics')}>Progress</button>
    </div>
    {activeTab === 'profile' ? <div className="profile-grid">
      <section className="surface-card profile-card">
        <div className="profile-card__heading"><i className="material-icons" aria-hidden="true">tune</i>
          <div><h2>Skill levels</h2><p>Choose the level that feels right for each skill.</p></div>
        </div>
        <div className="profile-levels">{LEVEL_FIELDS.map(([key, label]) => <label key={key}><span className="field-label">{label}</span><select className="browser-default field-control"
                                                                                                                                                 value={levels[key] || ''}
                                                                                                                                                 onChange={(event) => setLevels((current) => ({
                                                                                                                                                   ...current,
                                                                                                                                                   [key]: event.target.value
                                                                                                                                                 }))}>{LEVELS.map((level) => <option key={level}
                                                                                                                                                                                     value={level}>{level}</option>)}</select></label>)}</div>
        <div className="profile-card__actions">
          <button className="button-primary" disabled={!levelUpdated || waiting} onClick={saveLevels}>{waiting ? 'Saving…' : 'Save changes'}</button>
          <button className="button-danger" disabled={waiting} onClick={logout}><i className="material-icons" aria-hidden="true">logout</i> Sign out</button>
        </div>
      </section>
      <section className="surface-card profile-card review-card">
        <div className="profile-card__heading"><i className="material-icons" aria-hidden="true">event_repeat</i>
          <div><h2>Due today</h2><p>A short review keeps these words active.</p></div>
        </div>
        {words.length ? <ul className="review-list">{words.map((word) => <li key={word.id}>
          <div><strong>{word.wordValue}</strong><span>{word.wordRuValue}</span></div>
          <time dateTime={word.remindDate}>Due {new Date(word.remindDate).toLocaleDateString()}</time>
        </li>)}</ul> : <div className="review-empty"><i className="material-icons" aria-hidden="true">task_alt</i><p>You are all caught up for today.</p></div>}</section>
    </div> : <Statistics showNotification={showNotification} externalData={statistics} hideSummary/>}
  </main>;
};
export default User;
